import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import { InView } from 'react-intersection-observer';
import { v4 as uuidv4 } from 'uuid';

import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { libraryShape } from '@bibliocommons/bc-prop-types';

import ListCardAdapter from 'app/components/shared/ListCardAdapter';
import { listsHeroMappedProps } from 'app/shapes/browse/rows';

import './ListsHero.scss';

const makeImpressionId = (listId, impressionIdSuffix) => `${listId}-${impressionIdSuffix}`;

const ListsHero = ({
  additionalAnalyticsPayload,
  lists,
  library,
  title,
  trackListCreatorClick,
  trackListImageClick,
  trackListImpression,
  trackListTitleClick,
  userByListId
}) => {
  if (!lists.length) {
    return null;
  }

  const primaryListUuid = uuidv4();
  const primaryList = lists[0];
  const immutablePrimaryList = Immutable.fromJS(primaryList);
  const primaryListCreatorUser = Immutable.fromJS(userByListId[primaryList.id]);
  const secondaryLists = lists.slice(1);
  const uiContainerTitle = title;

  const handleVisibility = (itemPosition, list, impressionIdSuffix) => {
    return isVisible => {
      if (isVisible) {
        trackListImpression({
          ...additionalAnalyticsPayload,
          impressionId: makeImpressionId(list.id, impressionIdSuffix),
          itemPosition,
          list,
          uiContainerTitle
        });
      }
    };
  };

  return (
    <div className="cp-browse-lists-hero">
      <ScreenReaderMessage>
        <h2>{title}</h2>
      </ScreenReaderMessage>
      <div className="row">
        <div className="browse-lists-row__primary col-md-7">
          <InView threshold={1} triggerOnce onChange={handleVisibility(1, primaryList, primaryListUuid)}>
            <ListCardAdapter
              creator={primaryListCreatorUser}
              handleListImageClick={() => {
                trackListImageClick({
                  ...additionalAnalyticsPayload,
                  impressionId: makeImpressionId(primaryList.id, primaryListUuid),
                  itemPosition: 1,
                  list: primaryList,
                  uiContainerTitle
                });
              }}
              handleListTitleClick={() => {
                trackListTitleClick({
                  ...additionalAnalyticsPayload,
                  impressionId: makeImpressionId(primaryList.id, primaryListUuid),
                  itemPosition: 1,
                  list: primaryList,
                  uiContainerTitle
                });
              }}
              largeImage
              library={library}
              list={immutablePrimaryList}
              showDescription
              showUserBadge
              titleTag="h3"
              trackCreatorClick={({ linkText: creatorName }) => {
                trackListCreatorClick({
                  ...additionalAnalyticsPayload,
                  creatorName,
                  itemPosition: 1,
                  list: primaryList,
                  uiContainerTitle
                });
              }}
            />
          </InView>
        </div>

        <div className="browse-lists-row__secondary col-md-5">
          <ul className="browse-lists-row__secondary-items">
            {secondaryLists.map((list, index) => {
              const listId = list.id;
              const creatorUser = Immutable.fromJS(userByListId[listId]);
              const secondaryListsUuid = uuidv4();
              // + 2 because we want the primary list to be 1
              const itemPosition = index + 2;

              return (
                <li className="browse-lists-row__secondary-item" key={listId}>
                  <InView threshold={1} triggerOnce onChange={handleVisibility(itemPosition, list, secondaryListsUuid)}>
                    <ListCardAdapter
                      creator={creatorUser}
                      handleListImageClick={() => {
                        trackListImageClick({
                          ...additionalAnalyticsPayload,
                          impressionId: makeImpressionId(list.id, secondaryListsUuid),
                          itemPosition,
                          list,
                          uiContainerTitle
                        });
                      }}
                      handleListTitleClick={() => {
                        trackListTitleClick({
                          ...additionalAnalyticsPayload,
                          impressionId: makeImpressionId(list.id, secondaryListsUuid),
                          itemPosition,
                          list,
                          uiContainerTitle
                        });
                      }}
                      horizontal
                      list={Immutable.fromJS(list)}
                      library={library}
                      showUserBadge
                      titleTag="h3"
                      trackCreatorClick={({ linkText: creatorName }) => {
                        trackListCreatorClick({
                          ...additionalAnalyticsPayload,
                          creatorName,
                          itemPosition,
                          list,
                          uiContainerTitle
                        });
                      }}
                    />
                  </InView>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

ListsHero.propTypes = {
  additionalAnalyticsPayload: {}
};

ListsHero.propTypes = {
  ...listsHeroMappedProps,
  additionalAnalyticsPayload: PropTypes.object,
  library: libraryShape.isRequired,
  trackListCreatorClick: PropTypes.func.isRequired,
  trackListImageClick: PropTypes.func.isRequired,
  trackListImpression: PropTypes.func.isRequired,
  trackListTitleClick: PropTypes.func.isRequired
};

export default ListsHero;
